import * as React from 'react';
import type {Token} from '@sail/engine';
import {useTokenValue, animate} from '@sail/engine';
import {useUpdateEffect} from '@sail/react';
import {tokens} from '@sail/tokens/deprecated';
import {tokens as $} from '@sail/tokens';
import type {ToastProps} from './types';

const useTokenNumberValue = <T>(token: Token.Value<T>) => {
  return parseInt(useTokenValue(token) as string, 10);
};

export const translate = (placement: ToastProps['placement']) => {
  const offset = $['space.150'];
  const negativeOffset = `calc(-1 * ${offset})`;

  return `translate(0, ${placement === 'bottom' ? offset : negativeOffset})`;
};

interface AnimatedLayerProps {
  placement: ToastProps['placement'];
}

export const useAnimatedLayerIntents = ({placement}: AnimatedLayerProps) => {
  const easing = useTokenValue(tokens.animation.easing) as string;
  const medium = useTokenNumberValue(tokens.animation.duration.medium);
  const short = useTokenNumberValue(tokens.animation.duration.short);

  return React.useMemo(() => {
    return [
      animate.in({
        opacity: 0,
        transform: `scale(0.95) ${translate(placement)}`,
        easing,
        duration: medium,
      }),
      animate.out({
        opacity: 0,
        transform: `scale(0.95) ${translate(placement)}`,
        easing,
        duration: short,
      }),
    ];
  }, [placement, easing, medium, short]);
};

/**
 * Hook for managing a controlled timer that can be paused and resumed
 */
export function useControlledTimer({
  duration,
  onComplete,
}: {
  duration: number;
  onComplete: () => void;
}) {
  const [isPaused, setIsPaused] = React.useState(false);
  const [remainingTime, setRemainingTime] = React.useState(duration);
  const timeoutRef = React.useRef<NodeJS.Timeout | null>(null);
  const startTimeRef = React.useRef<number | null>(null);

  const clearTimer = React.useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  const startTimer = React.useCallback(() => {
    if (isPaused || remainingTime <= 0) {
      return;
    }

    startTimeRef.current = Date.now();
    timeoutRef.current = setTimeout(onComplete, remainingTime);
  }, [isPaused, remainingTime, onComplete]);

  const pause = React.useCallback(() => {
    if (!isPaused && timeoutRef.current && startTimeRef.current) {
      clearTimer();
      const elapsed = Date.now() - startTimeRef.current;
      setRemainingTime((prev) => Math.max(0, prev - elapsed));
      startTimeRef.current = null;
      setIsPaused(true);
    }
  }, [isPaused, clearTimer]);

  const resume = React.useCallback(() => {
    if (isPaused) {
      setIsPaused(false);
    }
  }, [isPaused]);

  const reset = React.useCallback(() => {
    clearTimer();
    setRemainingTime(duration);
    setIsPaused(false);
    startTimeRef.current = null;
  }, [duration, clearTimer]);

  useUpdateEffect(() => {
    reset();
  }, [duration, reset]);

  // Restart timer when resumed (only when transitioning from paused to not paused)
  const prevIsPausedRef = React.useRef(isPaused);

  React.useEffect(() => {
    const wasResumed = prevIsPausedRef.current && !isPaused;
    prevIsPausedRef.current = isPaused;

    if (wasResumed && remainingTime > 0 && startTimeRef.current === null) {
      startTimer();
    }
  }, [isPaused, remainingTime, startTimer]);

  // Cleanup on unmount
  React.useEffect(() => {
    return () => {
      clearTimer();
    };
  }, [clearTimer]);

  return {
    pause,
    resume,
    reset,
    start: startTimer,
    duration,
    isPaused,
    remainingTime,
  };
}
